import { Button } from 'components/Button/Button';
import { CollapsableFormZone } from 'components/Form/CollapsableFormZone/CollapsableFormZone';
import { TextInput } from 'components/Inputs/TextInput/TextInput';
import { HttpError } from 'services/api/types';
import { getSpacing } from 'stylesheet';

import { AccountBalanceSection } from './sections/AccountBalanceSection';
import { PaymentConfigurationSection } from './sections/PaymentConfigurationSection';
import { PaymentSettingsSection } from './sections/PaymentSettingsSection';
import { ButtonsContainer, FormRowContainer, SettingsFormContainer } from './SettingsForm.style';
import { SettingsFormKeys, SettingsInformation, SHOW_PAYMENT_SETTINGS } from './settingsFormValues';
import { useSettingsFieldHelpers } from './useSettingsFieldHelpers';
import { useSettingsFormik } from './useSettingsFormik';

type SettingsFormProps = {
  initialFormValues: SettingsInformation;
  onSubmit: (settingsInfo: SettingsInformation) => Promise<void>;
  onSubmitError: (error: HttpError) => void;
  onAbort: () => void;
  isReadOnly: boolean;
};

export const SettingsForm = ({
  initialFormValues,
  onSubmit,
  onSubmitError,
  onAbort,
  isReadOnly,
}: SettingsFormProps): JSX.Element => {
  const formik = useSettingsFormik(initialFormValues, onSubmit, onSubmitError);
  const { settingsFormIntl, getTextFieldProps, renderSwitch } = useSettingsFieldHelpers(
    formik,
    isReadOnly,
  );

  return (
    <SettingsFormContainer>
      <form onSubmit={formik.handleSubmit}>
        <CollapsableFormZone title={settingsFormIntl('sections.contact')}>
          <>
            <FormRowContainer>
              <TextInput
                {...getTextFieldProps(SettingsFormKeys.CONTACT_EMAIL, 'contact-email', true)}
                required
              />
            </FormRowContainer>
            <FormRowContainer>
              <TextInput
                {...getTextFieldProps(
                  SettingsFormKeys.SUBSCRIPTION_REQUEST_EMAIL,
                  'subscription-request-email',
                  true,
                )}
                required
              />
            </FormRowContainer>
          </>
        </CollapsableFormZone>

        {SHOW_PAYMENT_SETTINGS && (
          <>
            <PaymentSettingsSection formik={formik} isReadOnly={isReadOnly} />
            {formik.values[SettingsFormKeys.IS_PAYMENT_ENABLED] && (
              <PaymentConfigurationSection formik={formik} isReadOnly={isReadOnly} />
            )}
          </>
        )}
        <AccountBalanceSection formik={formik} isReadOnly={isReadOnly} />

        <CollapsableFormZone title={settingsFormIntl('sections.navigation')}>
          <>
            {renderSwitch(SettingsFormKeys.SHOW_INVOICES, 'show-invoices')}
            {renderSwitch(SettingsFormKeys.SHOW_DELIVERY_NOTES, 'show-delivery-notes')}
            {renderSwitch(SettingsFormKeys.SHOW_ACCOUNT_BALANCE, 'show-account-balance')}
          </>
        </CollapsableFormZone>

        <CollapsableFormZone title={settingsFormIntl('sections.notifications')}>
          {renderSwitch(
            SettingsFormKeys.IS_INVOICE_NOTIFICATION_ENABLED,
            'is-invoice-notification-enabled',
          )}
        </CollapsableFormZone>

        <CollapsableFormZone title={settingsFormIntl('sections.invitation-email')}>
          <>
            <FormRowContainer>
              <TextInput
                {...getTextFieldProps(
                  SettingsFormKeys.INVITATION_REPLY_TO_EMAIL,
                  'invitation-reply-to-email',
                  true,
                )}
              />
            </FormRowContainer>
            {renderSwitch(SettingsFormKeys.INVITATION_SHOW_USER_NAME, 'invitation-show-user-name')}
          </>
        </CollapsableFormZone>

        <ButtonsContainer>
          <Button
            displayStyle="unfilledGrey"
            onClick={onAbort}
            margins={isReadOnly ? undefined : `0 ${getSpacing(1)} 0 0`}
            disabled={formik.isSubmitting}
            bigPaddings
          >
            {settingsFormIntl(isReadOnly ? 'form.close' : 'form.cancel')}
          </Button>
          {!isReadOnly && (
            <Button
              displayStyle="filledOrange"
              type="submit"
              isLoading={formik.isSubmitting}
              bigPaddings
            >
              {settingsFormIntl('form.update')}
            </Button>
          )}
        </ButtonsContainer>
      </form>
    </SettingsFormContainer>
  );
};
